import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { useDataProvider, useNotify, useRefresh, useRecordContext, useGetList } from "react-admin";
import { ResourceName, PolicyStatus, type Policy, type Claimant } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { formatYearRange } from "@/utils/formatters/dateFormatter";

interface LinkPolicyDialogProps {
  open: boolean;
  onClose: () => void;
}

export const LinkPolicyDialog = ({ open, onClose }: LinkPolicyDialogProps) => {
  const claimant = useRecordContext<Claimant>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clientId = claimant?.client_id_ref;
  const linkedPolicyIds = new Set(claimant?.policies?.map((p) => p.id) ?? []);

  // Fetch all policies for this client
  const { data: allPolicies = [], isLoading } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!clientId && open }
  );

  // Exclude already-linked policies
  const availablePolicies = allPolicies.filter((p) => !linkedPolicyIds.has(p.id));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPolicy || !claimant) return;

    setIsSubmitting(true);
    try {
      await dataProvider.invoke(`claimants/${claimant.id}/policies/${selectedPolicy.id}`);
      notify("Policy linked successfully", { type: "success" });
      refresh();
      handleClose();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedPolicy(null);
    onClose();
  };

  // Detect policies that share the same carrier + date range and need disambiguation
  const duplicateKeys = new Set<string>();
  const seenKeys = new Map<string, number>();
  for (const p of availablePolicies) {
    const key = `${p.source_data?.policy_carrier}|${p.effective_start_date}|${p.effective_end_date}`;
    seenKeys.set(key, (seenKeys.get(key) ?? 0) + 1);
    if ((seenKeys.get(key) ?? 0) > 1) duplicateKeys.add(key);
  }

  const getPolicyLabel = (policy: Policy): string => {
    const carrier = policy.source_data?.policy_carrier || "Unknown Carrier";
    const yearRange = formatYearRange(policy.effective_start_date, policy.effective_end_date);
    const key = `${policy.source_data?.policy_carrier}|${policy.effective_start_date}|${policy.effective_end_date}`;
    const policyId = policy.source_data?.source_policy_id;
    return duplicateKeys.has(key) && policyId ? `${carrier} · ${yearRange} (${policyId})` : `${carrier} · ${yearRange}`;
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle variant="h3" sx={{ borderBottom: 1, borderColor: "divider" }}>
        Link Policy
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3, pb: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a policy to associate with this claimant. Only policies belonging to the same client are shown.
          </Typography>

          <Autocomplete
            options={availablePolicies}
            loading={isLoading}
            getOptionLabel={getPolicyLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedPolicy}
            onChange={(_e, newValue) => setSelectedPolicy(newValue)}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              const isActive = option.status === PolicyStatus.ACTIVE;
              return (
                <Box component="li" key={key} {...rest}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Typography variant="body2">{getPolicyLabel(option)}</Typography>
                    {isActive && (
                      <Chip
                        label="Active"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.65rem" }}
                      />
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Policy" placeholder="Search policies..." required size="small" />
            )}
            noOptionsText={
              allPolicies.length > 0 ? "All policies are already linked" : "No policies found for this client"
            }
          />
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: "divider", px: 3, py: 2 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={!selectedPolicy || isSubmitting}>
            Link Policy
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
